/**
 * Audit trail of what CorrectionAgent tried before surfacing the
 * decision to the operator. Collapsed by default; rendered inline
 * under each decision item in the field-scoped Inspector.
 */
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { CorrectionAttempt } from '../../../services/mappingApi';

export const CorrectionHistoryAccordion: React.FC<{ attempts: CorrectionAttempt[] }> = ({
  attempts,
}) => {
  if (!attempts || attempts.length === 0) return null;
  return (
    <Accordion
      disableGutters
      sx={{ mt: 1, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
        <Typography variant="caption" color="text.secondary">
          What CorrectionAgent tried ({attempts.length} round{attempts.length === 1 ? '' : 's'})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1 }}>
        <Stack spacing={1}>
          {attempts.map((a, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1,
                borderLeft: '2px solid rgba(0,0,0,0.16)',
                pl: 1,
              }}
            >
              <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                <Chip label={`round ${a.round}`} size="small" variant="outlined" />
                <Chip
                  label={a.outcome}
                  size="small"
                  color={
                    a.outcome === 'fixed'
                      ? 'success'
                      : a.outcome === 'abstained'
                        ? 'warning'
                        : 'default'
                  }
                />
                {a.abstain_reason ? (
                  <Chip
                    label={a.abstain_reason}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                ) : null}
              </Stack>
              {a.rationale ? (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {a.rationale}
                </Typography>
              ) : null}
              {a.proposed_mapping ? (
                <Box
                  component="pre"
                  sx={{
                    fontSize: '0.7rem',
                    mt: 0.5,
                    p: 0.5,
                    bgcolor: 'rgba(0,0,0,0.04)',
                    borderRadius: 0.5,
                    overflowX: 'auto',
                    margin: 0,
                  }}
                >
                  {JSON.stringify(a.proposed_mapping, null, 2)}
                </Box>
              ) : null}
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default CorrectionHistoryAccordion;
